import { supabase } from '../api/supabase'

// Job Group: thin wrappers over the database functions in migration 20261104a. ALL group logic (the rows, the per-destination totals,
// adding / editing / removing a row) lives in those functions; nothing here adds numbers up or decides anything -- the screen, the export
// and later invoicing all read the same answers.

export interface GroupRow {
  job_id: string
  job_ref: string
  job_ref_index: number | null
  status: string
  cancelled: boolean
  awb: string | null
  flight_no: string | null
  shipper_id: string | null
  shipper_name: string | null
  consignee_id: string | null
  consignee_name: string | null
  description: string | null
  from_port_id: string | null
  from_code: string | null
  from_name: string | null
  to_port_id: string | null
  to_code: string | null
  to_name: string | null
  cartons: number
  gross_weight: number
  chargeable_weight: number
  cbm: number
  pmc_count: number
  goods_lines: number
  notes_count: number
}

export interface GroupTotals {
  sort_key: 1 | 2 | 3            // 1 = one destination, 2 = rows with NO destination, 3 = month total
  dest_port_id: string | null
  dest_code: string | null
  dest_name: string | null
  is_unassigned: boolean
  is_total: boolean
  cartons: number
  gross_weight: number
  chargeable_weight: number
  cbm: number
  pmc_count: number
  shipments: number
}

// The fields a row edit may carry (same keys as group_update_row / group_add_row accept).
export type GroupRowPatch = Partial<{
  awb: string | null; flight_no: string | null; shipper_id: string | null; consignee_id: string | null; description: string | null
  from_port_id: string | null; to_port_id: string | null
  cartons: number | null; gross_weight: number | null; chargeable_weight: number | null; cbm: number | null; pmc_count: number | null
}>

// numeric / bigint columns can arrive as strings: normalise once, here.
const n = (v: unknown): number => (v === null || v === undefined || v === '' ? 0 : Number(v))

export async function fetchGroupRows(groupId: string): Promise<GroupRow[]> {
  const { data, error } = await supabase.rpc('group_rows', { p_group_id: groupId })
  if (error) throw new Error(error.message)
  return ((data ?? []) as GroupRow[]).map(r => ({
    ...r, cartons: n(r.cartons), gross_weight: n(r.gross_weight), chargeable_weight: n(r.chargeable_weight), cbm: n(r.cbm),
    pmc_count: n(r.pmc_count), goods_lines: n(r.goods_lines), notes_count: n(r.notes_count),
  }))
}

export async function fetchGroupTotals(groupId: string): Promise<GroupTotals[]> {
  const { data, error } = await supabase.rpc('group_destination_totals', { p_group_id: groupId })
  if (error) throw new Error(error.message)
  return ((data ?? []) as GroupTotals[]).map(t => ({
    ...t, cartons: n(t.cartons), gross_weight: n(t.gross_weight), chargeable_weight: n(t.chargeable_weight), cbm: n(t.cbm),
    pmc_count: n(t.pmc_count), shipments: n(t.shipments),
  }))
}

export async function addGroupRow(groupId: string, sourceJobId: string | null, values: GroupRowPatch = {}): Promise<{ id: string; job_ref: string }> {
  const { data, error } = await supabase.rpc('group_add_row', { p_group_id: groupId, p_source_job_id: sourceJobId, p_values: values })
  if (error) throw new Error(error.message)
  return data as { id: string; job_ref: string }
}

export async function updateGroupRow(jobId: string, patch: GroupRowPatch): Promise<void> {
  const { error } = await supabase.rpc('group_update_row', { p_job_id: jobId, p_patch: patch })
  if (error) throw new Error(error.message)
}

export async function setGroupRowCancelled(jobId: string, cancelled: boolean): Promise<void> {
  const { error } = await supabase.rpc('group_set_row_cancelled', { p_job_id: jobId, p_cancelled: cancelled })
  if (error) throw new Error(error.message)
}

// Pickers: server-side search (customers and ports are far too big to load).
export interface PickOption { id: string; label: string; code?: string; name?: string }

export async function searchAirports(term: string): Promise<PickOption[]> {
  const cleaned = term.trim().replace(/[%_,()]/g, '')
  let q = supabase.from('ports').select('id, code, name, un_locode').eq('is_active', true).eq('type', 'AIRPORT').order('code').limit(50)
  if (cleaned) q = q.or(`name.ilike.%${cleaned}%,code.ilike.${cleaned}%,un_locode.ilike.${cleaned}%`)
  const { data, error } = await q
  if (error) throw new Error(error.message)
  return ((data ?? []) as { id: string; code: string; name: string }[]).map(p => ({ id: p.id, code: p.code, name: p.name, label: `${p.code} - ${p.name}` }))
}
